import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, InputNumber, Tabs } from 'antd';
import DrawerModal from '../../shared/DrawerModal';
import styles from './CreateInviteCodeModal.styles';
import {
  createInviteCode,
  createInviteCodesBulk,
  selectCreatingInviteCode,
  selectSelectedCircle,
} from '../../../redux/store/userData/circles';
import { getIsVisibleModal, closeModal } from '../../../redux/store/ui/common';
import { useIntl } from 'react-intl';
import LoadingIcon from '../../shared/LoadingIcon';

const { TabPane } = Tabs;

const CreateInviteCodeModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedCircle = useSelector(selectSelectedCircle);
  const visibleModal = useSelector(getIsVisibleModal('createInviteCodeModal'));
  const creating = useSelector(selectCreatingInviteCode);

  const [activeTab, setActiveTab] = useState<'single' | 'bulk'>('single');
  const [description, setDescription] = useState('');
  const [count, setCount] = useState<number>(1);

  const prevCreating = useRef(false);

  // Reset fields whenever modal opens
  useEffect(() => {
    if (visibleModal) {
      setDescription('');
      setCount(1);
      setActiveTab('single');
    }
  }, [visibleModal]);

  // Auto-close modal when creation finishes
  useEffect(() => {
    if (prevCreating.current && !creating && visibleModal) {
      dispatch(closeModal('createInviteCodeModal'));
    }
    prevCreating.current = creating;
  }, [creating, visibleModal, dispatch]);

  const handleOnSubmit = (e: any) => {
    e.preventDefault();
    if (activeTab === 'bulk') {
      dispatch(
        createInviteCodesBulk(selectedCircle?.id!, description, count || 1)
      );
    } else {
      dispatch(createInviteCode(selectedCircle?.id!, description));
    }
  };

  const handleOnCancel = (e: any) => {
    e.preventDefault();
    if (creating) return;
    dispatch(closeModal('createInviteCodeModal'));
  };

  return (
    <>
      <DrawerModal
        visible={visibleModal}
        onClose={() => {
          if (creating) return;
          dispatch(closeModal('createInviteCodeModal'));
        }}
      >
        <div className="create-invite-code-modal">
          {creating ? (
            <div className="creating-wrapper">
              <LoadingIcon />
              <p className="creating-message">
                {intl.formatMessage({
                  id: 'fleetadmin.circle.inviteCodes.creating',
                  defaultMessage: 'Creating invite codes… Please wait.',
                })}
              </p>
            </div>
          ) : (
            <>
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as 'single' | 'bulk')}
              >
                <TabPane
                  tab={intl.formatMessage({
                    id: 'fleetadmin.circle.inviteCodes.single',
                    defaultMessage: 'Single',
                  })}
                  key="single"
                >
                  <div className="input-wrapper">
                    <p
                      data-testid="CreateInviteCodeModal-descriptionLabel"
                      className="label"
                    >
                      {intl.formatMessage({
                        id: 'fleetadmin.circle.inviteCodes.description',
                        defaultMessage: 'Description',
                      })}
                    </p>
                    <Input
                      data-testid="CreateInviteCodeModal-descriptionInput"
                      placeholder={intl.formatMessage({
                        id: 'fleetadmin.circle.inviteCodes.descriptionPlaceholder',
                        defaultMessage: 'Optional description',
                      })}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                </TabPane>
                <TabPane
                  tab={intl.formatMessage({
                    id: 'fleetadmin.circle.inviteCodes.bulk',
                    defaultMessage: 'Bulk',
                  })}
                  key="bulk"
                >
                  <div className="input-wrapper">
                    <p
                      data-testid="CreateInviteCodeModal-bulkDescriptionLabel"
                      className="label"
                    >
                      {intl.formatMessage({
                        id: 'fleetadmin.circle.inviteCodes.description',
                        defaultMessage: 'Description',
                      })}
                    </p>
                    <Input
                      data-testid="CreateInviteCodeModal-bulkDescriptionInput"
                      placeholder={intl.formatMessage({
                        id: 'fleetadmin.circle.inviteCodes.descriptionPlaceholder',
                        defaultMessage: 'Optional description',
                      })}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="input-wrapper">
                    <p className="label">
                      {intl.formatMessage({
                        id: 'fleetadmin.circle.inviteCodes.count',
                        defaultMessage: 'Count',
                      })}
                    </p>
                    <InputNumber
                      data-testid="CreateInviteCodeModal-countInput"
                      placeholder={intl.formatMessage({
                        id: 'fleetadmin.circle.inviteCodes.countPlaceholder',
                        defaultMessage: 'Number of codes to create',
                      })}
                      value={count}
                      min={1}
                      onChange={(v) => setCount(Number(v) || 1)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </TabPane>
              </Tabs>
              <Button
                data-testid="CreateInviteCodeModal-submitBtn"
                className="submit-button"
                onClick={handleOnSubmit}
              >
                {intl.formatMessage({ id: 'submit' })}
              </Button>
              <Button className="cancel-button" onClick={handleOnCancel}>
                {intl.formatMessage({ id: 'cancel' })}
              </Button>
            </>
          )}
        </div>
      </DrawerModal>
      <style jsx>{styles}</style>
    </>
  );
};

export default CreateInviteCodeModal;
